import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ProductCard from '@/components/base/ProductCard';
import ArticleCard from '@/components/base/ArticleCard';
import { products } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import { trustBadges } from '@/mocks/faqs';

const featuredProducts = [products[0], products[3], products[1], products[2]];
const latestArticles = articles.slice(0, 3);

export default function Home() {
  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '爱液官网',
    url: 'https://www.aiye-official.com/',
    description: '爱液品牌官方指定平台，提供正品润滑液产品及私密健康护理解决方案',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.aiye-official.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <SEOHead
        title="爱液官网 - 官方正品润滑液品牌中心 | 安全私密健康护理"
        description="爱液官网是品牌官方指定平台，提供正品爱液系列产品浏览、成分解析、使用指南及安全购买入口。了解爱液官网全线产品，享受专业私密健康护理解决方案，官网直营品质保障。"
        keywords="爱液官网,爱液官网正品购买,爱液官网产品系列,爱液官网使用方法,爱液润滑液官方,爱液官网优惠活动,爱液品牌官方旗舰"
        canonicalUrl="/"
        structuredData={homepageSchema}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20soft%20minimalist%20abstract%20background%20with%20gentle%20flowing%20silk%20like%20textures%20in%20warm%20cream%20and%20soft%20coral%20tones%2C%20dreamy%20atmospheric%20lighting%2C%20luxury%20wellness%20brand%20aesthetic%2C%20delicate%20organic%20curves%2C%20pure%20clean%20art%20direction%2C%20no%20text%2C%20no%20people%2C%20abstract%20fluid%20art%20style&width=1920&height=1080&seq=aiye-hero-bg-01&orientation=landscape"
            alt="爱液品牌 - 科学私护温柔以待"
            title="爱液官网 - 官方正品润滑液品牌中心"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white mb-4 md:mb-6 tracking-wide">
                科学私护，温柔以待
              </h1>
              <p className="text-base md:text-xl text-white/85 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
                爱液 - 专业私密健康护理品牌。全线产品通过国际权威认证，
                以科技与自然之力，为每一位女性带来安全、舒适、温柔的私护体验。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white text-base font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-bag-line text-lg"></i>
                  探索产品系列
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white text-base font-medium rounded-full hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/30"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解品牌故事
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {trustBadges.map((badge) => (
                <div key={badge.title} className="flex flex-col items-center text-center p-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className={`${badge.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">{badge.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{badge.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Overview */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-3">
                产品系列
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                从水润亲肤到修护滋养，从日常护理到高端体验 —— 总有一款适合你
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
              <Link
                to="/categories"
                className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300 p-8 md:p-10 text-center cursor-pointer"
              >
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
                  <i className="ri-drop-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">水基润滑液</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  天然水溶配方，清爽不黏腻，日常首选
                </p>
              </Link>
              <Link
                to="/categories"
                className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300 p-8 md:p-10 text-center cursor-pointer"
              >
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 group-hover:bg-accent-100 transition-colors">
                  <i className="ri-shining-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">硅基润滑液</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  极致丝滑持久，防水配方，高端体验
                </p>
              </Link>
              <Link
                to="/categories"
                className="group relative rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300 p-8 md:p-10 text-center cursor-pointer sm:col-span-2 lg:col-span-1"
              >
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 group-hover:bg-secondary-200 transition-colors">
                  <i className="ri-plant-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">天然植萃系列</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  100%植物萃取，极致温和，敏感肌与孕期之选
                </p>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="w-full bg-background-50 py-14 md:py-20" data-product-shop>
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-10 md:mb-14">
              <div>
                <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-2">
                  精选产品
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  爱液官网正品直营，品质保障
                </p>
              </div>
              <Link
                to="/categories"
                className="mt-4 sm:mt-0 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部产品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} {...product} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-3">
                  为什么选择爱液？
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  从配方研发到生产质检，每一个环节都精益求精，只为给你最安心的私密护理体验
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-award-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">国际权威认证</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        通过ISO 13485医疗器械质量管理体系、欧盟CE认证、SGS皮肤刺激性测试等多重国际权威认证。每一批次产品均经过严苛质检，确保安全无忧。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-flask-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">前沿科技配方</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        品牌私密健康研究院持续投入研发，三重分子量玻尿酸、神经酰胺修护、益生元微生态平衡等前沿技术，让私密护理与面部护肤同等级对待。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-heart-pulse-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">"六无"安全标准</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        坚持无对羟基苯甲酸酯、无石油基成分、无人工香精、无色素添加、无刺激性表面活性剂、无动物源性成分。从源头保障产品安全，让每一次使用都安心放心。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-user-heart-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">200万+用户信赖</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        连续两年天猫双十一润滑液品类销量前三，累计服务超过200万消费者。每一位用户的好评都是我们前进的动力。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-10 md:mb-14">
              <div>
                <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-2">
                  健康百科
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  专业私密健康知识，科学护理从这里开始
                </p>
              </div>
              <Link
                to="/articles"
                className="mt-4 sm:mt-0 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                阅读更多文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20soft%20minimalist%20abstract%20background%20with%20gentle%20flowing%20gradients%20in%20warm%20coral%20and%20cream%20tones%2C%20dreamy%20atmospheric%20lighting%2C%20luxury%20wellness%20brand%20aesthetic%2C%20delicate%20organic%20curves%2C%20pure%20clean%20artistic%20style%2C%20abstract%20fluid%20composition%2C%20soft%20feminine%20palette&width=1920&height=800&seq=aiye-cta-bg-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-500/60"></div>
          <div className="relative z-10 text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-heading font-semibold text-white mb-4">
              开启你的私密健康护理之旅
            </h2>
            <p className="text-base md:text-lg text-white/85 max-w-xl mx-auto mb-8 leading-relaxed">
              从今天开始，给自己的身体最温柔的呵护。爱液，陪伴每一位女性自信绽放。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-primary-600 text-base font-semibold rounded-full hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line text-lg"></i>
                选购产品
              </Link>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white text-base font-medium rounded-full hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/30"
              >
                <i className="ri-book-open-line text-lg"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}